import React, { useState, useMemo, useEffect, useRef } from "react";
import Pagination from "../../../../components/Pagination";
import { showClass, showGrade, showStudentURL } from "../../../../utils/textUtils";
import StudentMoreButton from "./StudentMoreButton";
import CustomCheckbox from "../../../../components/CustomCheckbox";
import DeleteStudentButton from "./DeleteStudentsButton";

const StudentTable = ({ students }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 14; // 페이지 당 학생 수

  const [selectedStudents, setSelectedStudents] = useState([]);

  const totalPages = Math.ceil(students.length / itemsPerPage);

  const currentStudents = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return students.slice(startIndex, endIndex);
  }, [students, currentPage, itemsPerPage]);

  // 페이지 번호 목록 만들기 (가령 1~5, 6~10처럼 가변으로)
  const visiblePages = useMemo(() => {
    if (totalPages === 0) return [];
    const startPage = Math.max(2, currentPage - 2); // 2부터 시작 (1은 항상 보여줌)
    const endPage = Math.min(totalPages - 1, currentPage + 2); // 마지막 페이지는 항상 별도 처리

    const pages = [];
    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    // 첫 페이지, 마지막 페이지 처리
    const withBoundaries = [1];
    if (startPage > 2) {
      withBoundaries.push("..."); // 첫 페이지와 시작 사이에 ...
    }
    withBoundaries.push(...pages);
    if (endPage < totalPages - 1) {
      withBoundaries.push("..."); // 끝 페이지와 마지막 사이에 ...
    }
    if (totalPages > 1) {
      withBoundaries.push(totalPages); // 마지막 페이지 추가
    }

    return withBoundaries;
  }, [currentPage, totalPages]);

  // 페이지 변경 핸들러
  const handlePageClick = (page) => {
    setCurrentPage(page);
  };

  // **체크박스 관련 로직**

  // (1) 개별 학생 체크박스 핸들러
  const handleCheckboxChange = (checked, student) => {
    if (checked) {
      // 체크 O
      setSelectedStudents((prev) => [...prev, student]);
    } else {
      // 체크 해제
      setSelectedStudents((prev) =>
        prev.filter((prevStudent) => prevStudent.id !== student.id)
      );
    }
  };

  // (2) 헤더(전체선택) 체크박스 핸들러
  const handleHeaderCheckboxChange = (checked) => {
    if (checked) {
      setSelectedStudents(currentStudents);
    } else {
      // 해제
      setSelectedStudents([]);
    }
  };

  // (3) 헤더 체크박스가 "완전히 체크" 상태인지 판별 (현재 페이지 모두 체크된 경우)
  const isHeaderChecked =
    currentStudents.length > 0 &&
    currentStudents.every((currentStudent) =>
      selectedStudents.some(
        (selectedStudent) => selectedStudent.id === currentStudent.id
      )
    );

  const overlayStyle = {
    position: "fixed",
    bottom: "20px",
    left: "50%",
    transform: "translateX(-50%)",
    backgroundColor: "#0E141B",
    color: "#fff",
    padding: "14px 24px",
    borderRadius: "999px",
    boxShadow: "0 14px 40px rgba(14,20,27,.35)",
    textAlign: "center",
    zIndex: 9999,
  };

  return (
    <div
      style={{
        width: Math.min(window.innerWidth, window.screen.width) <= 768 ? "100%" : "90%",
        border: "1px solid #E6EDEC",
        borderRadius: "18px",
        // [모바일 수정] 열 붕괴 대신 좌우 스와이프.
        // 데스크톱은 hidden 이었는데, 부모(StudentManagementPage)가 높이를
        // 화면으로 잘라서 화면 밖 학생으로 내려갈 방법이 없었다. 세로만 연다.
        overflowX: Math.min(window.innerWidth, window.screen.width) <= 768 ? "auto" : "hidden",
        overflowY: "auto",
        maxHeight: Math.min(window.innerWidth, window.screen.width) <= 768 ? undefined : "calc(100vh - 200px)",
        WebkitOverflowScrolling: "touch",
        boxShadow: "0 10px 30px -22px rgba(20,40,50,.22)",
        backgroundColor: "#fff",
      }}
    >
      <table
        className="data-table"
        style={{
          minWidth: Math.min(window.innerWidth, window.screen.width) <= 768 ? "760px" : undefined,
        }}
      >
        <colgroup>
          <col style={{ width: "48px" }} />
          <col style={{ width: "90px" }} />
          <col style={{ width: "56px" }} />
          <col />
          <col style={{ width: "132px" }} />
          <col style={{ width: "132px" }} />
          <col style={{ width: "88px" }} />
          <col style={{ width: "44px" }} />
        </colgroup>
        <tbody>
          <tr className="data-table__header-row">
            <td
              style={{
                textAlign: "center",
              }}
            >
              <CustomCheckbox
                checked={isHeaderChecked}
                onChange={(e) => handleHeaderCheckboxChange(e.target.checked)}
              />
            </td>
            <td>이름</td>
            <td>학년</td>
            <td>학원 반</td>
            <td>전화번호 (학생)</td>
            <td>전화번호 (학부모)</td>
            <td className="cell-center">학습 페이지</td>
            <td></td>
          </tr>
          {currentStudents.map((student, index) => {
            const isChecked = selectedStudents.includes(student);
            return (
              <tr key={index}>
                <td
                  style={{
                    textAlign: "center",
                  }}
                >
                  <CustomCheckbox
                    checked={isChecked}
                    onChange={(e) =>
                      handleCheckboxChange(e.target.checked, student)
                    }
                  />
                </td>
                <td className="cell-strong">
                  {student.session ? (
                    <a
                      href={showStudentURL(student.session)}
                      target="_blank"
                      rel="noopener noreferrer"
                      title="학생 페이지 열기"
                      className="cell-link"
                    >
                      {student.name}
                    </a>
                  ) : (
                    student.name
                  )}
                </td>
                <td>{showGrade(student.grade)}</td>
                <td>
                  {showClass(student.level_list)}
                </td>
                <td className="cell-numeric">{student.student_phone}</td>
                <td className="cell-numeric">{student.parent_phone}</td>
                <td className="cell-center">
                  {student.session ? (
                    <button
                      onClick={() => window.open(showStudentURL(student.session), '_blank')}
                      className="button-outline"
                    >
                      열기
                    </button>
                  ) : (
                    <span className="cell-empty">-</span>
                  )}
                </td>
                <td>
                  <StudentMoreButton student={student} />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {/* 페이지네이션 UI */}
      <Pagination
        visiblePages={visiblePages}
        current_page={currentPage}
        handlePageClick={handlePageClick}
      />

      {/* 하단 Overlay: 선택된 학생 수가 1명 이상이면 표시 */}
      {selectedStudents.length > 0 && (
        <div style={overlayStyle}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "20px",
            }}
          >
            <strong style={{ fontSize: "0.9rem" }}>학생 <span style={{ color: "#00D9C0" }}>{selectedStudents.length}명</span> 선택됨</strong>
            <DeleteStudentButton
              students={selectedStudents}
              setSelectedStudents={setSelectedStudents}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default StudentTable;
